import { useState } from "react";
import { CopyButton } from "@/components/copyButton";
import { RegenerateIcon } from "@/assets/icons";
import { useMutation } from "@tanstack/react-query";
import { regenerateToken } from "@/helpers/apis";
import { loginUser } from "@/services/auth";
import RegenerateModal from "../regenerateModal";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from "@altimateai/lego";

interface SettingsCardWithRegenerateProps {
  cardTitle: string;
  cardText: string;
  copyText: string;
  showRegenerateSuccess: () => void;
  setAuthToken: (token: string) => void;
}

const SettingsCardWithRegenerate = ({
  cardTitle,
  cardText,
  copyText,
  showRegenerateSuccess,
  setAuthToken,
}: SettingsCardWithRegenerateProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const cardId = `settings-card-${cardTitle.toLowerCase().replace(/\s+/g, "-")}`;

  const { mutate } = useMutation({
    mutationFn: regenerateToken,
    onSuccess: data => {
      if (data) {
        showRegenerateSuccess();
        toggleModal();
        setAuthToken(data.auth_token);
        loginUser({
          ...data,
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          id: data.id!,
          role_FK: data.role_FK ?? undefined,
        });
      }
    },
  });

  const toggleModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const handleRegenerate = () => {
    mutate();
  };

  return (
    <>
      <Card className="al-w-1/2">
        <CardHeader id={cardId}>
          <Typography variant="section-heading">{cardTitle}</Typography>
        </CardHeader>
        <CardContent>
          <Stack direction="column">
            <Typography variant="body" className="al-text-dark-gray">
              <span aria-describedby={cardId}>{cardText}</span>
            </Typography>
            <div
              role="textbox"
              aria-label={cardTitle}
              aria-readonly="true"
              className="al-bg-base-surface al-p-4 al-w-full al-rounded-md al-text-dark-gray"
            >
              {copyText}
            </div>
            <Stack justify="between" className="al-w-full al-mt-2">
              <CopyButton value={copyText} aria-label={`Copy ${cardTitle.toLowerCase()}`} />
              <Button
                variant="secondary"
                onClick={toggleModal}
                aria-label="Regenerate API key"
                aria-haspopup="dialog"
              >
                <RegenerateIcon />
                Regenerate
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Dialog open={isModalOpen} onOpenChange={toggleModal} aria-labelledby={`${cardId}-dialog`}>
        <DialogContent>
          <RegenerateModal handleRegenerate={handleRegenerate} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SettingsCardWithRegenerate;
